import { defineSlotRecipe } from '@chakra-ui/react';
import { selectAnatomy } from '@chakra-ui/react/anatomy';

import { selectItemMinHeightPx, selectTriggerHeightPx } from './select.kds';

const itemSizeStyles = (size: keyof typeof selectItemMinHeightPx) => ({
  minH: `${selectItemMinHeightPx[size]}px`,
  px: '2',
});

/** KDS Select overrides — see `figmaComponentNodes.select` in theme/primitives/source.ts. */
export const selectSlotRecipe = defineSlotRecipe({
  slots: selectAnatomy.keys(),
  base: {
    control: {
      borderRadius: 'md',
      borderWidth: '1px',
      borderColor: 'neutral.250',
      bg: 'neutral.0',
      _hover: {
        borderColor: 'neutral.350',
      },
      '&:has(:focus-visible)': {
        borderWidth: '2px',
        borderColor: 'brand.300',
        outline: 'none',
      },
      '&:has([aria-expanded=true])': {
        borderColor: 'brand.500',
      },
      '&:has(:disabled)': {
        bg: 'neutral.50',
        borderColor: 'neutral.200',
        color: 'neutral.350',
        opacity: 1,
      },
      '&:has([aria-invalid=true])': {
        borderColor: 'red.500',
      },
    },
    trigger: {
      textStyle: 'default.regular',
      gap: '1',
      justifyContent: 'flex-start',
      border: 'none',
      focusVisibleRing: 'none',
      outline: 'none',
    },
    valueText: {
      color: 'neutral.500',
      '&[data-placeholder-shown]': {
        color: 'neutral.250',
      },
    },
    content: {
      borderRadius: 'md',
      bg: 'neutral.0',
      borderWidth: '1px',
      borderColor: 'neutral.100',
      boxShadow: 'menu',
      p: '0',
      focusVisibleRing: 'none',
      _focusVisible: {
        outline: 'none',
        boxShadow: 'none',
      },
    },
    list: {
      p: '0',
      gap: '0',
      focusVisibleRing: 'none',
      outline: 'none',
      _focusVisible: {
        outline: 'none',
        boxShadow: 'none',
      },
    },
    itemGroup: {
      p: '0',
      gap: '0',
    },
    item: {
      borderRadius: '0',
      textStyle: 'default.regular',
      color: 'neutral.500',
      _highlighted: {
        bg: 'brand.50',
        color: 'neutral.500',
      },
      '&[data-state=checked]': {
        bg: 'brand.100',
        color: 'brand.500',
      },
    },
    indicator: {
      color: 'neutral.250',
      _icon: {
        boxSize: '20px',
      },
      _open: {
        transform: 'rotate(180deg)',
      },
    },
    label: {
      textStyle: 'default.medium',
      color: 'neutral.500',
      fontWeight: 'medium',
    },
  },
  variants: {
    size: {
      sm: {
        root: {
          '--select-trigger-height': `${selectTriggerHeightPx.sm}px`,
          '--select-trigger-padding-x': 'spacing.2',
        },
        control: { h: `${selectTriggerHeightPx.sm}px` },
        trigger: {
          textStyle: 'default.regular',
        },
        item: itemSizeStyles('sm'),
      },
      md: {
        root: {
          '--select-trigger-height': `${selectTriggerHeightPx.md}px`,
          '--select-trigger-padding-x': 'spacing.2',
        },
        control: { h: `${selectTriggerHeightPx.md}px` },
        trigger: {
          textStyle: 'default.regular',
        },
        item: itemSizeStyles('md'),
      },
      lg: {
        root: {
          '--select-trigger-height': `${selectTriggerHeightPx.lg}px`,
          '--select-trigger-padding-x': 'spacing.2',
        },
        control: { h: `${selectTriggerHeightPx.lg}px` },
        trigger: {
          textStyle: 'large.regular',
        },
        item: {
          ...itemSizeStyles('lg'),
          textStyle: 'large.regular',
        },
      },
    },
    variant: {
      outline: {},
    },
  },
  defaultVariants: {
    size: 'sm',
    variant: 'outline',
  },
});
